if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.actualMediaCount = 0; // 存储实际媒体数量
        this.hasInitializedProgress = false; // 防止重复初始化
      }

      connectedCallback() {
        // 获取实际媒体数量（不包含克隆/重复的）
        this.calculateActualMediaCount();
        
        // 当组件被插入DOM后，初始化进度条
        this.initProgressBar();
      }

      /**
       * 计算实际媒体数量（排除克隆/重复的）
       */
      calculateActualMediaCount() {
        // 方法1：从swiper-component获取实际幻灯片数量
        const swiperComponent = this.querySelector('swiper-component');
        if (swiperComponent && swiperComponent.initSwiper) {
          const swiper = swiperComponent.initSwiper;
          // 获取原始幻灯片数量（排除克隆）
          if (swiper.slides && swiper.slides.length > 0) {
            // 排除克隆幻灯片（通常有swiper-slide-duplicate类）
            const originalSlides = Array.from(swiper.slides).filter(
              slide => !slide.classList.contains('swiper-slide-duplicate')
            );
            this.actualMediaCount = originalSlides.length;
            return;
          }
        }
        
        // 方法2：从特定的数据结构获取
        const mediaElements = this.querySelectorAll('[data-media-id]');
        if (mediaElements.length > 0) {
          // 去重：使用Set存储唯一的media-id
          const uniqueMediaIds = new Set();
          mediaElements.forEach(el => {
            const mediaId = el.getAttribute('data-media-id');
            if (mediaId) uniqueMediaIds.add(mediaId);
          });
          this.actualMediaCount = uniqueMediaIds.size;
        } else {
          // 方法3：回退到原始计数
          this.actualMediaCount = mediaElements.length;
        }
      }

      /**
       * Initialize and sync the progress bar for carousel layout
       */
      initProgressBar() {
        // 防止重复初始化
        if (this.hasInitializedProgress) return;
        
        // 仅对轮播布局且存在进度条的元素生效
        if (!this.classList.contains('media-gallery--carousel')) return;
        
        const progressBar = this.querySelector('.slideshow-progress__bar');
        if (!progressBar) return;

        const swiperComponent = this.querySelector('swiper-component');
        if (!swiperComponent) return;

        // 使用实际媒体数量，如果未计算则回退到DOM计数
        const totalSlides = this.actualMediaCount > 0 
          ? this.actualMediaCount 
          : this.querySelectorAll('[data-media-id]').length;
          
        if (totalSlides <= 1) return;

        const singleBarWidth = 100 / totalSlides;

        // 独立的重试机制，等待Swiper就绪
        const waitForSwiperAndBind = (retries = 20) => {
          if (swiperComponent.initSwiper) {
            const swiper = swiperComponent.initSwiper;
            
            // 验证实际幻灯片数量
            let actualSlideCount = totalSlides;
            if (swiper.slides && swiper.slides.length > 0) {
              // 获取非克隆幻灯片的实际索引
              const originalSlides = Array.from(swiper.slides).filter(
                slide => !slide.classList.contains('swiper-slide-duplicate')
              );
              actualSlideCount = originalSlides.length;
              
              // 如果发现数量不一致，更新进度计算
              if (actualSlideCount !== totalSlides) {
                console.warn(`检测到幻灯片数量不一致: DOM=${totalSlides}, 实际=${actualSlideCount}`);
              }
            }
            
            // 定义进度更新函数
            const updateProgress = () => {
              const currentIndex = swiper.activeIndex;
              
              // 处理循环模式下的索引
              let adjustedIndex = currentIndex;
              if (swiper.params.loop) {
                // 循环模式下，activeIndex可能会超出实际数量
                adjustedIndex = currentIndex % actualSlideCount;
              }
              
              const offset = adjustedIndex * 100;
              progressBar.style.width = `${singleBarWidth}%`;
              progressBar.style.transform = `translateX(${offset}%)`;
            };

            // 初始设置一次
            updateProgress();

            // 绑定到滑页变更事件
            swiper.on('slideChange', updateProgress);
            
            // 标记为已初始化
            this.hasInitializedProgress = true;
            
            console.debug(`进度条初始化完成: 幻灯片数量=${actualSlideCount}, 进度块宽度=${singleBarWidth}%`);
          } else if (retries > 0) {
            // Swiper未就绪，继续重试
            setTimeout(() => waitForSwiperAndBind(retries - 1), 50);
          } else {
            console.warn('初始化进度条失败：Swiper实例在超时时间内未就绪。');
          }
        };

        waitForSwiperAndBind();
      }

      setActiveMedia(mediaId, smooth = false) {
        if (!mediaId) return false;

        let cleanMediaId = mediaId;
        if (mediaId.indexOf('-') !== -1) {
          const parts = mediaId.split('-');
          cleanMediaId = parts[parts.length - 1];
        }

        let element = this.querySelector(`[data-media-id="${cleanMediaId}"]`);
        
        if (!element) {
          element = this.querySelector(`[data-media-id="${mediaId}"]`);
        }

        if (!element) return false;

        this.querySelectorAll('[data-media-id].active').forEach((node) => node.classList.remove('active'));

        element.classList.add('active');

        const allMediaElements = Array.from(this.querySelectorAll('[data-media-id]'));
        const mediaIndex = allMediaElements.indexOf(element);

        this.syncSlideshow(element, mediaIndex, smooth);
        this.syncThumbnails(element, mediaIndex);

        if (this.classList.contains('media-gallery--grid')) {
          this.swapGridMedia(element, mediaIndex);
        }

        return true;
      }

      /**
       * Sync slideshow/swiper component when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       * @param {boolean} smooth - Whether to use smooth transition
       */
      syncSlideshow(mediaElement, mediaIndex, smooth = false) {
        try {
          if (!this.classList.contains('media-gallery--carousel')) return;

          const swiperComponent = this.querySelector('swiper-component');
          if (!swiperComponent) return;
          
          const waitForSwiper = (retries = 10) => {
            if (swiperComponent.initSwiper) {
              const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
              let slideIndex = mediaIndex;
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              }

              if (slideIndex >= 0 && slideIndex < slides.length && swiperComponent.initSwiper) {
                swiperComponent.initSwiper.slideTo(slideIndex, smooth ? 800 : 0);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForSwiper(retries - 1), 50);
            }
          };

          waitForSwiper();
        } catch (e) {
          try {
            if (this.slideshow && typeof this.slideshow.goTo === 'function') {
              this.slideshow.goTo(mediaIndex);
            }
          } catch (err) {
            // silent
          }
        }
      }

      /**
       * Sync thumbnails controls when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      syncThumbnails(mediaElement, mediaIndex) {
        try {
          const thumbnailsContainer = this.querySelector('.swiper-controls__thumbnails-container');
          if (!thumbnailsContainer) return;

          const thumbnailButtons = Array.from(thumbnailsContainer.querySelectorAll('.swiper-controls__thumbnail[data-index]'));
          if (thumbnailButtons.length === 0) return;

          let slideIndex = mediaIndex;
          
          if (this.classList.contains('media-gallery--carousel')) {
            const swiperComponent = this.querySelector('swiper-component');
            if (swiperComponent) {
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              } else {
                if (swiperComponent.initSwiper && typeof swiperComponent.initSwiper.activeIndex === 'number') {
                  slideIndex = mediaIndex;
                }
              }
            }
          }

          let targetThumbnail = thumbnailButtons.find((button) => {
            const index = parseInt(button.getAttribute('data-index'), 10);
            return !isNaN(index) && index === slideIndex;
          });

          if (!targetThumbnail) {
            targetThumbnail = thumbnailButtons.find((button) => {
              const index = parseInt(button.getAttribute('data-index'), 10);
              return !isNaN(index) && index === mediaIndex;
            });
          }

          if (targetThumbnail) {
            const finalSlideIndex = parseInt(targetThumbnail.getAttribute('data-index'), 10);

            this.syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, finalSlideIndex);
          }
        } catch (e) {
          // silent
        }
      }

      /**
       * Sync thumbnail swiper to show the active thumbnail
       * @param {HTMLElement} thumbnailsContainer - The thumbnail container element
       * @param {Array} thumbnailButtons - Array of all thumbnail buttons
       * @param {HTMLElement} targetThumbnail - The active thumbnail button
       * @param {number} slideIndex - The slide index to sync to
       */
      syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, slideIndex) {
        try {
          const waitForThumbnailSwiper = (retries = 10) => {
            const swiperComponent = this.querySelector('swiper-component');
            let thumbsSwiper = null;

            if (swiperComponent && swiperComponent.initSwiper && swiperComponent.initSwiper.params && swiperComponent.initSwiper.params.thumbs) {
              thumbsSwiper = swiperComponent.initSwiper.params.thumbs.swiper;
            }

            if (!thumbsSwiper) {
              const thumbnailSwiper = thumbnailsContainer.querySelector('.swiper');
              if (thumbnailSwiper && thumbnailSwiper.swiper) {
                thumbsSwiper = thumbnailSwiper.swiper;
              }
            }

            if (thumbsSwiper) {
              if (slideIndex >= 0 && slideIndex < thumbnailButtons.length) {
                thumbsSwiper.slideTo(slideIndex, 300);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForThumbnailSwiper(retries - 1), 50);
            }
          };

          waitForThumbnailSwiper();
        } catch (e) {
          // silent
        }
      }

      /**
       * Swap media in grid layout: move variant image to first position
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      swapGridMedia() {
        try {
          if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
            const handleVariantUpdateOnce = (event) => {
              try {
                const source = event?.detail?.data?.html;
                if (!source) return;

                const newMediaGallery = source.querySelector('media-gallery');
                if (!newMediaGallery) return;
                this.replaceWith(newMediaGallery);

                try {
                  newMediaGallery.scrollIntoView({ behavior: 'smooth' });
                } catch (e) {
                  // ignore scrolling errors
                }
              } catch (e) {
                // silent
              } finally {
                document.removeEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
              }
            };
            document.addEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
          }
          
        } catch (e) {
          // silent
        }
      }
    }
  );
}